import { useState, useEffect, useRef } from "react";
import { Link, useLocation, Navigate } from "react-router-dom";
import { Bell, List as ListIcon, User, Gear, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Sidebar } from "./Sidebar";
import { GlobalSearch } from "./GlobalSearch";
import api, { API } from "../lib/api";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ScrollArea } from "./ui/scroll-area";
import TrialCountdown from "./TrialCountdown";
import CreditsBanner from "./CreditsBanner";
import GuidedTour from "./GuidedTour";

export const Layout = ({ children, user, onLogout }) => {
  const location = useLocation();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const reconnectDelayRef = useRef(5000);  // exponential backoff starts at 5s

  // First-run detector flow guard — if the user has a connected store
  // and HASN'T seen the FindFirstInfluencerFlow yet, force them to `/`
  // regardless of which protected route they tried to land on (settings,
  // orders, influencers, etc). This is the "second time I've told you"
  // bug: users who completed the WP plugin connect handshake while on
  // /settings (or any other tab) were ending up back on settings instead
  // of the detector flow because the dashboard's `?from=plugin` redirect
  // only fires on Dashboard mount — not on background tabs whose URL
  // never changed.
  //
  // Routes the guard does NOT redirect away from:
  //   - "/" itself (already on the right place)
  //   - "/pricing"  (paywall flow — needs_card users routing to upgrade)
  //   - "/notifications" (asynchronous push surface)
  //   - "/admin"   (admin only — orthogonal)
  //
  // The flag we read is the SAME one Dashboard.jsx uses to decide whether
  // to render <FindFirstInfluencerFlow>: `id_celebrated_store_<storeId>`
  // OR `user.detection_screen_seen_at`. Both being absent = "never saw
  // the celebration → bring me to it now".
  const storeId = user?.store_id;
  const serverSeen = !!user?.detection_screen_seen_at;
  let localSeen = false;
  try {
    localSeen = storeId && localStorage.getItem(`id_celebrated_store_${storeId}`) === "1";
  } catch {}
  const needsDetectorFlow = !!storeId && !serverSeen && !localSeen;
  const SAFE_PATHS = new Set(["/", "/pricing", "/notifications", "/admin"]);
  const shouldRedirect = needsDetectorFlow && !SAFE_PATHS.has(location.pathname);

  // WebSocket connection for real-time notifications
  useEffect(() => {
    const connectWs = () => {
      // Strip trailing `/api` (NOT the first occurrence — that would eat the
      // `/api.` inside the hostname `api.influencedetector.com`, producing the
      // broken `wss:.influencedetector.com/api/api/ws/notifications` we hit in
      // prod). Anchor the regex to end-of-string so we only touch the path
      // suffix, never the host.
      const baseUrl = API.replace(/\/api$/, "").replace(/^http/, "ws");
      // WebSockets can't send Authorization headers in browsers, so the JWT
      // rides in the query string. The server decodes it to scope this socket
      // to the right store_id — critical for multi-tenant isolation so one
      // tenant's live notifications don't leak to another's bell panel.
      const token = localStorage.getItem("id_token") || "";
      if (!token) return;  // no session → don't open a socket at all
      const url = `${baseUrl}/api/ws/notifications?token=${encodeURIComponent(token)}`;

      try {
        const ws = new WebSocket(url);
        wsRef.current = ws;

        ws.onopen = () => {
          // Reset the reconnect backoff on every successful connect so a
          // stable connection heals the exponential ramp.
          reconnectDelayRef.current = 5000;
          // Send periodic pings
          const pingInterval = setInterval(() => {
            if (ws.readyState === WebSocket.OPEN) {
              ws.send("ping");
            }
          }, 25000);
          ws._pingInterval = pingInterval;
        };

        ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            // Broadcast ALL WS events as a generic window event so any component can subscribe.
            window.dispatchEvent(new CustomEvent("id:ws", { detail: data }));

            if (data.type === "notification") {
              setNotifications(prev => [data, ...prev].slice(0, 10));
              setUnreadCount(prev => prev + 1);
              toast.info(data.title, { description: data.message });
              // Broadcast specific notification types so other components (e.g. Sidebar
              // watchlist badge) can react in real-time without waiting for polling.
              if (data.notification_type) {
                window.dispatchEvent(
                  new CustomEvent("id:notification", { detail: data })
                );
              }
            }
          } catch (e) {
            // ignore non-JSON messages
          }
        };

        ws.onclose = () => {
          if (ws._pingInterval) clearInterval(ws._pingInterval);
          // Reconnect with exponential backoff, capped at 60s. Previously we
          // retried every 5s forever; on broken hosts this hammered both the
          // server and the client CPU with TLS handshakes.
          const delay = reconnectDelayRef.current;
          reconnectDelayRef.current = Math.min(delay * 2, 60000);
          reconnectTimerRef.current = setTimeout(connectWs, delay);
        };

        ws.onerror = () => {
          ws.close();
        };
      } catch (e) {
        console.debug("WebSocket connection failed, using polling fallback");
      }
    };

    connectWs();

    return () => {
      if (wsRef.current) {
        if (wsRef.current._pingInterval) clearInterval(wsRef.current._pingInterval);
        wsRef.current.close();
      }
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
    };
  }, []);

  // Initial fetch + polling fallback — pauses when the tab is hidden so idle
  // windows don't generate background API traffic.
  useEffect(() => {
    const fetchNotifications = async () => {
      if (document.hidden) return;
      try {
        const res = await api.getNotifications({ unread_only: true, limit: 5 });
        setNotifications(res.data);
        setUnreadCount(res.data.length);
      } catch (e) {
        console.error("Failed to fetch notifications", e);
      }
    };
    fetchNotifications();
    // Bumped from 30s → 60s. WebSocket is the real-time path; polling is a
    // safety net, and 60s is plenty for a safety net.
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, []);

  // First-run detector flow guard — placed AFTER hooks (rules-of-hooks
  // forbids early-returning before useEffect). See the comment block at
  // the top of the component for the full rationale.
  if (shouldRedirect) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F3F4F6]">
      <Sidebar
        currentPath={location.pathname}
        user={user}
        onLogout={onLogout}
        mobileOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      {/* On mobile no left margin (sidebar slides over); on desktop reserve 16rem */}
      <div className="md:ml-64">
        <header className="bg-white border-b border-black/10 px-4 md:px-6 py-3 md:py-4 sticky top-0 z-30" data-testid="header">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {/* Hamburger — mobile-only */}
              <button
                type="button"
                onClick={() => setMobileNavOpen(true)}
                className="md:hidden -ml-1 p-2 text-[#0A0A0A] hover:bg-black/5 rounded-md"
                aria-label="Open menu"
                data-testid="mobile-menu-trigger"
              >
                <ListIcon size={22} weight="bold" />
              </button>
              <h2 className="text-base md:text-xl font-bold tracking-tight text-[#0A0A0A] font-heading truncate">
                {location.pathname === "/" && "Dashboard"}
                {location.pathname === "/orders" && "Orders"}
                {location.pathname === "/influencers" && "Customer Influencers"}
                {location.pathname === "/potential-influencers" && "Subscriber Influencers"}
                {location.pathname === "/social-followers" && "Social Followers"}
                {location.pathname === "/name-lookup" && "Name Lookup"}
                {location.pathname === "/watchlist" && "Watchlist"}
                {location.pathname === "/notifications" && "Notifications"}
                {location.pathname === "/settings" && "Settings"}
                {location.pathname === "/admin" && "Admin"}
              </h2>
            </div>

            <div className="flex items-center gap-2 md:gap-4 shrink-0">
              {/* TrialCountdown hidden on mobile to save space — full-width on /pricing/checkout pages instead */}
              <div className="hidden sm:block">
                <TrialCountdown user={user} />
              </div>
              <GlobalSearch />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" className="relative border-black/15 rounded-none" data-testid="notification-bell">
                    <Bell size={20} weight="regular" />
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#DC2626] text-white text-[10px] font-bold flex items-center justify-center">
                        {unreadCount}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-[calc(100vw-2rem)] sm:w-80 rounded-none border-black/15">
                  <div className="p-3 border-b border-black/10">
                    <p className="text-sm font-bold text-[#0A0A0A]">Notifications</p>
                  </div>
                  <ScrollArea className="h-64">
                    {notifications.length === 0 ? (
                      <div className="p-4 text-center text-sm text-[#525252]">No new notifications</div>
                    ) : (
                      notifications.map((n) => (
                        <DropdownMenuItem key={n.id} className="p-3 cursor-pointer rounded-none focus:bg-black/5">
                          <div>
                            <p className="text-sm font-medium text-[#0A0A0A]">{n.title}</p>
                            <p className="text-xs text-[#525252] mt-1">{n.message}</p>
                          </div>
                        </DropdownMenuItem>
                      ))
                    )}
                  </ScrollArea>
                  <div className="p-2 border-t border-black/10">
                    <Link to="/notifications" className="block text-center text-sm font-medium text-[#4A7A00] hover:underline">
                      View all notifications
                    </Link>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Account menu — universal Sign Out + Settings entry point
                  so users don't have to hunt for it in the sidebar (which
                  is collapsed behind a hamburger on mobile). Renders the
                  user's initials when we have a name, otherwise a generic
                  user icon. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="icon"
                    className="relative border-black/15 rounded-none"
                    data-testid="account-menu-trigger"
                    aria-label="Account menu"
                  >
                    {(() => {
                      const label = user?.company_name || user?.email || "";
                      const initials = label
                        .split(/[\s@.]+/)
                        .filter(Boolean)
                        .slice(0, 2)
                        .map((s) => s[0]?.toUpperCase() || "")
                        .join("");
                      return initials ? (
                        <span className="text-xs font-bold tracking-wider text-[#0A0A0A]" data-testid="account-menu-initials">
                          {initials}
                        </span>
                      ) : (
                        <User size={20} weight="regular" />
                      );
                    })()}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-64 rounded-none border-black/15">
                  <DropdownMenuLabel className="px-3 py-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#737373]">
                      Signed in as
                    </div>
                    <div className="text-sm font-bold text-[#0A0A0A] truncate" data-testid="account-menu-email">
                      {user?.email || "—"}
                    </div>
                    {user?.company_name && (
                      <div className="text-xs text-[#525252] truncate mt-0.5">
                        {user.company_name}
                      </div>
                    )}
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator className="bg-black/10" />
                  <DropdownMenuItem asChild className="rounded-none focus:bg-black/5 cursor-pointer">
                    <Link
                      to="/settings"
                      className="flex items-center gap-2 px-3 py-2 text-sm text-[#0A0A0A]"
                      data-testid="account-menu-settings"
                    >
                      <Gear size={16} /> Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-black/10" />
                  <DropdownMenuItem
                    onSelect={() => {
                      // Defer to next tick so the menu animates closed
                      // before the auth state flips and unmounts Layout.
                      setTimeout(() => onLogout?.(), 0);
                    }}
                    className="rounded-none focus:bg-red-50 cursor-pointer text-[#B91C1C] focus:text-[#B91C1C]"
                    data-testid="account-menu-logout"
                  >
                    <div className="flex items-center gap-2 px-1 py-1 text-sm font-medium w-full">
                      <SignOut size={16} /> Sign out
                    </div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Credits banner — sticky under the header, only renders at 80%+ usage */}
        <CreditsBanner />

        <main className="p-4 md:p-6">
          {children}
        </main>
      </div>

      {/* Global guided tour — auto-triggers once after the user's first
          influencer detection. Self-rendering, persists 'completed'
          state in localStorage so it never re-shows on refresh. */}
      <GuidedTour user={user} />
    </div>
  );
};
